/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import * as React from 'react';
import { SidePaneEntitySelectionStore } from 'src/luis/appShell/buildPane/intentPage/stores/SidePaneEntitySelectionStore';
import { Brush } from 'src/luis/modules/labeling/components/Brush';
import { resolveBrushRules } from 'src/luis/modules/labeling/utils/brushRules';
import { useModels } from 'src/luis/modules/models/useModels';
import { entityDenormalizer } from 'src/luis/projects/entities/utils/entitiesDenormalizer';
import { BrushStore } from 'src/luis/projects/utterances/stores/BrushStore';
import { LabelingMode } from 'src/luis/projects/utterances/stores/LabelingStore';
import { resolveEntityStore } from 'src/luis/projects/utterances/utils/utteranceUtils';

export type BrushContainerProps = {
    attachedToRef: React.MutableRefObject<HTMLElement>;
    brushStore: BrushStore;
    labelingMode: LabelingMode;
    sidePaneEntitySelectionStore: SidePaneEntitySelectionStore;
};

export const BrushContainer = observer((props: BrushContainerProps) => {
    const { attachedToRef, brushStore, labelingMode, sidePaneEntitySelectionStore } = props;
    const { entityStores, intentStores, phraseListStores, isLoaded } = useModels();

    const getDenormalizedEntity = entityDenormalizer(entityStores, intentStores, phraseListStores);
    const attachedData = sidePaneEntitySelectionStore.selection.entityId
        && isLoaded && {
            ...resolveEntityStore({
                entityStores,
                entityId: sidePaneEntitySelectionStore.selection.entityId,
                parentId: sidePaneEntitySelectionStore.selection.parentId,
                roleId: sidePaneEntitySelectionStore.selection.roleId
            }),
            normalizedValue: sidePaneEntitySelectionStore.selection.normalizedValue
        };

    const denormalizedBrushAttachedData = {
        entity: attachedData?.entity && getDenormalizedEntity(attachedData.entity),
        parentEntity: attachedData?.parentEntity && getDenormalizedEntity(attachedData.parentEntity),
        role: attachedData?.role,
        normalizedValue: attachedData?.normalizedValue
    };

    const hoveredEntitiesIds = entityStores.filter(e => brushStore.hoveredEntitiesIds.find(id => id === e.id)).map(e => e.id as string);

    const brushState = resolveBrushRules({ data: attachedData, hoveredEntitiesIds });

    brushStore.setIsValidState(brushState === 'valid');

    return (
        <Brush state={brushState} hidden={labelingMode !== 'brush'} data={denormalizedBrushAttachedData} attachedToRef={attachedToRef} />
    );
});
